import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import qs from 'querystringify';
import {
  Button,
  Card,
  Flex,
  FormField,
  InlineMessage,
  Select,
  RouterLink,
  Text,
  TextInput,
} from '../../components';
import api from '../../utils/services';

/* If no planId is passed, then save will create a new plan
 * If a planId is passed, it will not update on change,
 * it will only use the planId during componentWillMount
 */
const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      carrierId: PropTypes.string.isRequired,
    }),
  }).isRequired,
  planId: PropTypes.string,
  languageId: PropTypes.number.isRequired,
};

const defaultProps = {
  planId: undefined,
};


// TODO general error handleing
class PlanForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      plan: {
        id: 0,
        name: '',
        cmsContractId: '',
        cmsPbpId: '',
        regionId: 0,
        cmsSegmentId: '',
        cmsYear: '',
      },
      regions: null,
      saving: false,
      errors: null,
      success: '',
    };
  }

  componentDidMount() {
    const { planId, match: { params: { carrierId } }, languageId } = this.props;
    const requests = [api.getRegionsByCarrierId(qs.stringify({ carrierId }, true))];
    if (planId) {
      requests.push(api.getPlanByIdAndLanguage(planId, qs.stringify({ languageId }, true)));
    }

    Promise.all(requests)
      .then(([regionsResponse, planResponse]) => {
        const regions = regionsResponse;
        this.setState({
          regions,
          loading: false,
        });
        if (planId) {
          const plan = planResponse;
          plan.regionId = plan.region.id;
          this.setState({ plan });
        }
      });
  }

  handleChange = (event) => {
    const { target: { name, value } } = event;
    const { errors } = this.state;
    if (errors !== null) {
      delete errors[name];
    }
    this.setState(state => ({
      plan: {
        ...state.plan,
        [name]: value,
      },
    }));
    this.handleValidate(name, value);
  }

  handleSelectChange = ({ value }) => {
    this.setState(state => ({
      plan: {
        ...state.plan,
        regionId: parseInt(value, 10),
      },
    }));
    this.handleSuccess('regionId');
  }

  handleSubmit = (event) => {
    event.preventDefault();
    this.setState({ saving: true });
    const { history, match: { params: { carrierId } } } = this.props;
    const { plan } = this.state;
    let hasErrors = false;
    // Check for validation
    Object.keys(plan).forEach((k) => {
      if (this.handleValidate(k, plan[k])) {
        hasErrors = true;
      }
    });
    if (hasErrors) {
      this.setState(state => ({
        saving: false,
        errors: { ...state.errors, planError: 'Please fix the errors in the form.' },
        success: '',
      }));
      return;
    }
    const payload = { ...plan, carrierId };
    delete payload.id;

    if (plan.id) {
      api.updatePlan(plan.id, payload)
        .catch(() => {
          this.setState({ saving: false, errors: { planError: 'Error in updating the plan, try again! ' }, success: '' });
        }).finally(() => {
          this.setState({ saving: false, success: 'Plan successfully updated.', errors: null });
        });
    } else {
      api.addPlan(payload)
        .then(() => {
          history.push(`/carriers/${carrierId}/plans`);
        }).catch(() => {
          this.setState({ saving: false, errors: { planError: 'Error in saving the plan, try again! ', success: '' } });
        });
    }
  }

  handleSuccess = (name) => {
    this.setState(state => ({
      errors: {
        ...state.errors,
        [name]: {
          caption: '',
          valid: true,
          validated: true,
          variant: 'success',
        },
      },
    }));
  }

  handleValidate = (name, value) => {
    let hasErrors = false;
    switch (name) {
      case 'name':
        if (value.length === 0) {
          this.setState(state => ({
            errors: {
              ...state.errors,
              [name]: {
                caption: 'Plan should not be empty',
                valid: false,
                validated: true,
                variant: 'danger',
              },
            },
          }));
          hasErrors = true;
        } else {
          this.handleSuccess(name);
        }
        break;

      case 'cmsContractId':
        if (value.length !== 5) {
          this.setState(state => ({
            errors: {
              ...state.errors,
              [name]: {
                caption: 'CMS Contract Id should be 5 characters',
                valid: false,
                validated: true,
                variant: 'danger',
              },
            },
          }));
          hasErrors = true;
        } else {
          this.handleSuccess(name);
        }
        break;

      case 'cmsPbpId':
        if (value.length !== 3) {
          this.setState(state => ({
            errors: {
              ...state.errors,
              [name]: {
                caption: 'CMS PBP Id should be 3 characters',
                valid: false,
                validated: true,
                variant: 'danger',
              },
            },
          }));
          hasErrors = true;
        } else {
          this.handleSuccess(name);
        }
        break;

      case 'cmsSegmentId':
        if (value.length !== 3) {
          this.setState(state => ({
            errors: {
              ...state.errors,
              [name]: {
                caption: 'CMS Segment Id should be 3 characters',
                valid: false,
                validated: true,
                variant: 'danger',
              },
            },
          }));
          hasErrors = true;
        } else {
          this.handleSuccess(name);
        }
        break;

      case 'regionId':
        if (value === 0) {
          this.setState(state => ({
            errors: {
              ...state.errors,
              [name]: {
                caption: 'Region should be selected',
                valid: false,
                validated: true,
                variant: 'danger',
              },
            },
          }));
          hasErrors = true;
        } else {
          this.handleSuccess(name);
        }
        break;

      case 'cmsYear': {
        if (!/^\d{4}$/.test(value)) {
          this.setState(state => ({
            errors: {
              ...state.errors,
              [name]: {
                caption: 'Invalid plan year',
                valid: false,
                validated: true,
                variant: 'danger',
              },
            },
          }));
          hasErrors = true;
        } else {
          this.handleSuccess(name);
        }
        break;
      }

      default:
        break;
    }
    return hasErrors;
  }


  render() {
    const {
      loading,
      regions,
      plan,
      saving,
      errors,
      success,
    } = this.state;
    const { match: { params: { carrierId } } } = this.props;
    let name; let cmsContractId; let cmsPbpId; let regionId; let planError;
    let cmsSegmentId; let cmsYear;
    if (errors !== null) {
      ({
        name, cmsContractId, cmsPbpId, regionId, planError, cmsSegmentId, cmsYear,
      } = errors);
    }
    let regionOptions;
    if (regions) {
      regionOptions = regions.map(region => ({
        text: region.name,
        value: `${region.id}`,
      }));
    } else {
      regionOptions = [];
    }

    if (!loading && regions !== null && regions.length === 0) {
      return (
        <Card>
          <Card.Block>
            <Text>Please add a Region here before adding a Plan</Text>
            <RouterLink href={`/carriers/${carrierId}/plans/regions/new`}>
              <Button primary>Add Region</Button>
            </RouterLink>
          </Card.Block>
        </Card>
      );
    }

    return (
      <Card loading={loading}>
        <Card.Block>
          <InlineMessage message={success} color="green" fieldId="plan" />
          <InlineMessage message={planError} color="red" fieldId="plan" />
          <form onSubmit={this.handleSubmit}>
            <FormField
              label="Name"
              input={TextInput}
              name="name"
              value={plan.name}
              onChange={this.handleChange}
              caption={name ? name.caption : ''}
              // eslint-disable-next-line no-nested-ternary
              invalid={name ? (name.validated ? !name.valid : undefined) : undefined}
              variant={name ? name.variant : undefined}
            />
            <Flex>
              <Flex.Item grow={1}>
                <FormField
                  label="CMS Contract Id"
                  input={TextInput}
                  name="cmsContractId"
                  value={plan.cmsContractId}
                  onChange={this.handleChange}
                  caption={cmsContractId ? cmsContractId.caption : ''}
                  // eslint-disable-next-line no-nested-ternary
                  invalid={cmsContractId
                    ? (cmsContractId.validated ? !cmsContractId.valid : undefined)
                    : undefined}
                  variant={cmsContractId ? cmsContractId.variant : undefined}
                />
              </Flex.Item>
              <Flex.Item grow={1}>
                <FormField
                  label="CMS PBP Id"
                  input={TextInput}
                  name="cmsPbpId"
                  value={plan.cmsPbpId}
                  onChange={this.handleChange}
                  caption={cmsPbpId ? cmsPbpId.caption : ''}
                  // eslint-disable-next-line no-nested-ternary
                  invalid={cmsPbpId
                    ? (cmsPbpId.validated ? !cmsPbpId.valid : undefined)
                    : undefined}
                  variant={cmsPbpId ? cmsPbpId.variant : undefined}
                />
              </Flex.Item>
              <Flex.Item grow={1}>
                <FormField
                  label="CMS Segment Id"
                  input={TextInput}
                  name="cmsSegmentId"
                  value={plan.cmsSegmentId}
                  onChange={this.handleChange}
                  caption={cmsSegmentId ? cmsSegmentId.caption : ''}
                  // eslint-disable-next-line no-nested-ternary
                  invalid={cmsSegmentId
                    ? (cmsSegmentId.validated ? !cmsSegmentId.valid : undefined)
                    : undefined}
                  variant={cmsSegmentId ? cmsSegmentId.variant : undefined}
                />
              </Flex.Item>
            </Flex>
            <Flex>
              <Flex.Item grow={1}>
                <FormField
                  label="Plan Year"
                  input={TextInput}
                  name="cmsYear"
                  value={plan.cmsYear.toString()}
                  onChange={this.handleChange}
                  caption={cmsYear ? cmsYear.caption : ''}
                  // eslint-disable-next-line no-nested-ternary
                  invalid={cmsYear ? (cmsYear.validated ? !cmsYear.valid : undefined) : undefined}
                  variant={cmsYear ? cmsYear.variant : undefined}
                />
              </Flex.Item>
              <Flex.Item grow={1}>
                <FormField
                  label="Region"
                  input={Select}
                  data={regionOptions}
                  name="regionid"
                  placeholder={regions ? 'Select a region...' : 'Loading...'}
                  selectedItem={regionOptions.find(r => r.value === `${plan.regionId}`)}
                  onChange={this.handleSelectChange}
                  caption={regionId ? regionId.caption : ''}
                  // eslint-disable-next-line no-nested-ternary
                  invalid={regionId
                    ? (regionId.validated ? !regionId.valid : undefined) : undefined}
                  variant={regionId ? regionId.variant : undefined}
                />
              </Flex.Item>
            </Flex>
            <Flex paddingTop="1em">
              <Flex.Item marginStart="auto">
                <Button primary loading={saving} type="submit">Save Plan</Button>
              </Flex.Item>
            </Flex>
          </form>
        </Card.Block>
      </Card>
    );
  }
}

PlanForm.propTypes = propTypes;
PlanForm.defaultProps = defaultProps;
export default withRouter(PlanForm);
